import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { FileText, DollarSign, Calendar } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import DatePicker from './DatePicker';

const AddLicenseDialog = ({ open, onClose, onSuccess, state }) => {
  console.log('AddLicenseDialog render - open:', open, 'state:', state);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    license_type: '',
    category: '',
    license_number: '',
    issue_date: '',
    expiration_date: '',
    fee: ''
  });

  const licenseTypes = [
    'Managing General Agent (MGA)',
    'Surplus Lines',
    'General Lines (P&C) License'
  ];

  const handleDateChange = (field, value) => {
    // Format as MM/DD/YYYY while typing
    let cleaned = value.replace(/\D/g, ''); // Remove non-digits
    
    if (cleaned.length >= 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5) {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9);
    }
    
    setFormData(prev => ({ ...prev, [field]: cleaned }));
  };

  const convertToISODate = (mmddyyyy) => {
    if (!mmddyyyy || mmddyyyy.length !== 10) return '';
    const [month, day, year] = mmddyyyy.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.license_type || !formData.category || !formData.license_number || !formData.expiration_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        entity_id: 'ENT-LOSSPAY-001',
        state: state,
        license_type: formData.license_type,
        license_number: formData.license_number,
        issue_date: formData.issue_date ? convertToISODate(formData.issue_date) : new Date().toISOString().split('T')[0],
        effective_date: formData.issue_date ? convertToISODate(formData.issue_date) : new Date().toISOString().split('T')[0],
        expiration_date: convertToISODate(formData.expiration_date),
        status: 'active',
        owner: 'compliance@losspay.com',
        fee: formData.fee ? parseFloat(formData.fee) : null
      };

      await api.createLicense(payload);
      
      // Auto-create expense if fee exists
      if (formData.fee && parseFloat(formData.fee) > 0) {
        try {
          const expensePayload = {
            entity_id: 'ENT-LOSSPAY-001',
            title: `${formData.license_type} License - ${state}`,
            description: `License fee for ${formData.license_type} in ${state}`,
            state: state,
            category: formData.category === 'Resident' || formData.category === 'Non-Resident' ? 'License Application' : 'License Renewal',
            amount: parseFloat(formData.fee),
            payment_date: formData.issue_date ? convertToISODate(formData.issue_date) : new Date().toISOString().split('T')[0],
            status: 'paid'
          };
          console.log('Creating expense with payload:', expensePayload);
          await api.createExpense(expensePayload);
          console.log('Expense created successfully');
          toast.success('Expense entry created automatically');
        } catch (expenseError) {
          console.error('Failed to create expense entry:', expenseError);
          toast.error('Note: Failed to create expense entry');
          // Don't fail the license creation if expense fails
        }
      }
      
      toast.success(`${formData.license_type} license added successfully for ${state}`);
      
      // Reset form
      setFormData({
        license_type: '',
        category: '',
        license_number: '',
        issue_date: '',
        expiration_date: '',
        fee: ''
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create license:', error);
      toast.error('Failed to create license. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-license-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <FileText className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <DialogTitle className="text-xl">Add State License</DialogTitle>
              <DialogDescription className="mt-1">
                Add a new license for <Badge variant="secondary" className="ml-1">{state}</Badge>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* License Type */}
          <div className="space-y-2">
            <Label htmlFor="license-type" className="text-sm font-medium">
              License Type <span className="text-rose-500">*</span>
            </Label>
            <Select 
              value={formData.license_type} 
              onValueChange={(value) => handleChange('license_type', value)}
              data-testid="license-type-select"
            >
              <SelectTrigger id="license-type" className="w-full">
                <SelectValue placeholder="Select license type" />
              </SelectTrigger>
              <SelectContent>
                {licenseTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Select the primary license type</p>
          </div>

          {/* Category (Resident/Non Resident) */}
          <div className="space-y-2">
            <Label htmlFor="category" className="text-sm font-medium">
              License Category <span className="text-rose-500">*</span>
            </Label>
            <Select 
              value={formData.category} 
              onValueChange={(value) => handleChange('category', value)}
              data-testid="category-select"
            >
              <SelectTrigger id="category" className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Resident">Resident</SelectItem>
                <SelectItem value="Non Resident">Non Resident</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Resident or Non Resident license</p>
          </div>

          {/* License Number */}
          <div className="space-y-2">
            <Label htmlFor="license-number" className="text-sm font-medium">
              License Number <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="license-number"
              type="text"
              value={formData.license_number}
              onChange={(e) => handleChange('license_number', e.target.value)}
              placeholder="e.g., MGA-123456"
              data-testid="license-number-input"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">Enter the official license number</p>
          </div>

          {/* Date Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="issue-date" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Issue Date
              </Label>
              <DatePicker
                id="issue-date"
                value={formData.issue_date}
                onChange={(value) => handleChange('issue_date', value)}
                placeholder="MM/DD/YYYY"
                data-testid="issue-date-input"
              />
              <p className="text-xs text-muted-foreground">When license was issued</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiration-date" className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Expiration Date <span className="text-rose-500">*</span>
              </Label>
              <DatePicker
                id="expiration-date"
                value={formData.expiration_date}
                onChange={(value) => handleChange('expiration_date', value)}
                placeholder="MM/DD/YYYY"
                data-testid="expiration-date-input"
              />
              <p className="text-xs text-muted-foreground">License expiration date</p>
            </div>
          </div>

          {/* Fee */}
          <div className="space-y-2">
            <Label htmlFor="fee" className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-3.5 w-3.5" />
              License Fee
            </Label>
            <Input
              id="fee"
              type="number"
              step="0.01"
              min="0"
              value={formData.fee}
              onChange={(e) => handleChange('fee', e.target.value)}
              placeholder="0.00"
              data-testid="fee-input"
            />
            <p className="text-xs text-muted-foreground">Annual or renewal fee amount (optional)</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              data-testid="submit-license-button"
              className="w-full sm:w-auto"
            >
              {loading ? 'Adding License...' : 'Add License'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLicenseDialog;
